import { ReactNode } from 'react';
import { useAuth } from '../../hooks/useAuth';

interface AuthGuardProps {
  /**
   * The content to render when auth is ready
   */
  children: ReactNode;

  /**
   * Optional fallback to render while auth is loading
   * @default <div>Loading...</div>
   */
  fallback?: ReactNode;
}

/**
 * AuthGuard component that conditionally renders children only when authentication
 * state has been fully resolved.
 *
 * Use this component to wrap any content that should only be displayed after
 * we know whether the user is authenticated or not.
 *
 * @example
 * // Basic usage
 * <AuthGuard>
 *   <AvatarProfile />
 * </AuthGuard>
 *
 * @example
 * // With custom loading fallback
 * <AuthGuard fallback={<Spinner />}>
 *   <UserProfile />
 * </AuthGuard>
 */
export const AuthGuard = ({ children, fallback = <div>Loading...</div> }: AuthGuardProps) => {
  const { isReady } = useAuth();

  if (!isReady) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};
